import { useEffect, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { InfoIcon } from "./InfoIcon.tsx";

// OptionsDialog.tsx's own NumberField is a different shape (string-valued,
// keeps its fields as raw editable strings) — not a duplicate of this one.

/**
 * Bare `<input>` counterpart to `NumberField` below, no `<label>` wrapper —
 * for a grid/table cell that already has its own external labeling (a `<th>`
 * header, a property-panel row) rather than needing one of its own. Same
 * buffered-text behavior: a bare "-" or a trailing "." mid-edit isn't wiped
 * out by the controlled numeric `value` prop before it becomes a valid
 * number, and a fully-cleared field doesn't snap back to `0` — unlike a
 * plain `<input type="number" value={n} onChange={(e) =>
 * onChange(Number(e.target.value))}/>`, where `Number("")` is `0` and a
 * leading `-` alone never parses, making both untypable.
 */
export function NumberInput({
  value,
  onChange,
  ...rest
}: {
  value: number;
  onChange: (value: number) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type">) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <input
      type="text"
      inputMode="decimal"
      {...rest}
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        const parsed = Number(next);
        if (next.trim() !== "" && Number.isFinite(parsed)) onChange(parsed);
      }}
    />
  );
}

export function NumberField({
  label,
  info,
  unit,
  value,
  onChange,
}: {
  label: string;
  /** Tooltip explanation (InfoIcon.tsx) — only pass it where the field's
      meaning genuinely isn't obvious, e.g. a SWMM formula coefficient. */
  info?: string;
  unit?: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const input = <NumberInput value={value} onChange={onChange} />;
  return (
    <label className="options-field">
      <span>
        {label}
        {info && <InfoIcon text={info} />}
      </span>
      {unit ? (
        <span className="options-field-row">
          {input}
          <span className="options-field-unit">{unit}</span>
        </span>
      ) : (
        input
      )}
    </label>
  );
}

/** A closed set of choices — each option's value is also its display text. */
export function SelectField({
  label,
  info,
  value,
  onChange,
  options,
}: {
  label: string;
  info?: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
}) {
  return (
    <label className="options-field">
      <span>
        {label}
        {info && <InfoIcon text={info} />}
      </span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

export function TextField({
  label,
  info,
  value,
  onChange,
}: {
  label: string;
  info?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="options-field">
      <span>
        {label}
        {info && <InfoIcon text={info} />}
      </span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/** A checkbox-gated optional section. UnitHydrographsEditor.tsx keeps its own separate `<div>`/`<h5>` variant at a different heading depth — not a duplicate to merge with this one. */
export function LayerSection({
  title,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: ReactNode;
}) {
  return (
    <section className="property-panel-group">
      <h4 className="named-collection-subsection-title lid-layer-toggle">
        <label>
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
          {title}
        </label>
      </h4>
      {children}
    </section>
  );
}
